import { useErrors } from '@auth0/auth0-acul-react/reset-password';
import Cta from 'creative-ui/uikit/Cta';
import CtaContainer from 'creative-ui/uikit/CtaContainer';
import InputPassword from 'creative-ui/uikit/Input/InputPassword';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { AuthErrorBanner } from '@/components/AuthErrorBanner/AuthErrorBanner';
import { Flex } from '@/components/Layout/Flex/Flex';
import { PasswordCheck } from '@/components/PasswordCheck/PasswordCheck';
import { useTrackErrors } from '@/hooks/useTrackErrors';
import { useResetPasswordManager } from '@/screens/reset-password/hooks/useResetPasswordManager';

interface ResetPasswordFormData {
  newPassword: string;
}

export function ResetPasswordForm() {
  const { t } = useTranslation('resetPassword');

  const {
    control,
    handleSubmit,
    formState: { errors, isSubmitting },
    watch,
  } = useForm<ResetPasswordFormData>({
    defaultValues: {
      newPassword: '',
    },
    mode: 'onChange',
  });

  const newPassword = watch('newPassword');

  const { handleResetPassword, passwordValidation } =
    useResetPasswordManager(newPassword);

  const { isValid: passwordIsValid, results: passwordPolicyResults } =
    passwordValidation;

  const { errors: sdkErrors } = useErrors();
  const { generalErrorMessages } = useTrackErrors(
    sdkErrors,
    'change/password/error',
  );

  const onSubmit = async (formData: ResetPasswordFormData) => {
    if (!passwordIsValid) {
      return;
    }
    await handleResetPassword(formData.newPassword);
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Flex direction="column" gapY="16px">
        <AuthErrorBanner errors={generalErrorMessages} />
        <Controller
          name="newPassword"
          control={control}
          rules={{
            required: t('resetPasswordForm.errorRequired'),
            minLength: {
              value: 8,
              message: t('resetPasswordForm.errorMinLength'),
            },
          }}
          render={({ field }) => (
            <>
              <InputPassword
                {...field}
                placeholder={t('resetPasswordForm.passwordPlaceholder')}
                status={errors.newPassword ? 'error' : undefined}
                statusMessage={errors.newPassword?.message}>
                {t('resetPasswordForm.passwordLabel')}
              </InputPassword>
              <PasswordCheck
                headerText={t('resetPasswordForm.passwordCheckHeader')}
                passwordComplexityRules={passwordPolicyResults}
                showValidation={newPassword.length > 0}
              />
            </>
          )}
        />

        <CtaContainer stack>
          <Cta
            className="c-submitCta"
            type="primary"
            submit
            disabled={isSubmitting}
            isWaiting={isSubmitting}>
            {t('resetPasswordForm.continueButtonText')}
          </Cta>
        </CtaContainer>
      </Flex>
    </form>
  );
}
